import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useResume } from './ResumeContext';
import { useParams } from 'react-router-dom';
import { useAppContext } from '../../../context/AppContext';

const AutosaveContext = createContext(null);

export function AutosaveProvider({ children, authToken }) {
  const { activeResume, isDirty, setIsDirty } = useResume();
  const { resumeId } = useParams();
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | dirty | saving | saved | error
  const [lastSaved, setLastSaved] = useState(null);
  
  const { setAutosaveStatus } = useAppContext();

  useEffect(() => {
    if (setAutosaveStatus) {
      setAutosaveStatus(saveStatus);
    }
  }, [saveStatus, setAutosaveStatus]);
  
  const timerRef = useRef(null);

  // Keep refs of the latest values to access them in the unmount cleanup safely
  const activeResumeRef = useRef(activeResume);
  const isDirtyRef = useRef(isDirty);
  const resumeIdRef = useRef(resumeId);
  const authTokenRef = useRef(authToken);

  useEffect(() => {
    activeResumeRef.current = activeResume;
    isDirtyRef.current = isDirty;
    resumeIdRef.current = resumeId;
    authTokenRef.current = authToken;
  }, [activeResume, isDirty, resumeId, authToken]);

  useEffect(() => {
    if (isDirty) {
      setSaveStatus('dirty');
      
      if (timerRef.current) clearTimeout(timerRef.current);
      
      timerRef.current = setTimeout(async () => {
        setSaveStatus('saving');
        try {
          const docId = resumeId || 'master';
          const docType = (resumeId && resumeId !== 'master') ? 'variant' : 'master';
          
          const res = await fetch('/api/workspace/document', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              action: 'save_resume',
              payload: activeResume,
              active_document: {
                id: docId,
                type: docType
              }
            })
          });
          
          if (!res.ok) {
            throw new Error('Save failed');
          }
          
          setIsDirty(false);
          setSaveStatus('saved');
          setLastSaved(new Date());
        } catch (err) {
          setSaveStatus('error');
        }
      }, 1500);
    }
    
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [activeResume, isDirty, resumeId, authToken, setIsDirty]);

  // Emergency save on unmount if dirty
  useEffect(() => {
    return () => {
      if (isDirtyRef.current && activeResumeRef.current && authTokenRef.current) {
        const docId = resumeIdRef.current || 'master';
        const docType = (resumeIdRef.current && resumeIdRef.current !== 'master') ? 'variant' : 'master';
        
        console.log(`[Autosave] Component unmounting with unsaved changes. Performing emergency save for ${docId}...`);
        
        fetch('/api/workspace/document', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authTokenRef.current}`
          },
          body: JSON.stringify({
            action: 'save_resume',
            payload: activeResumeRef.current,
            active_document: {
              id: docId,
              type: docType
            }
          }),
          keepalive: true
        }).catch(err => {
          console.error('[Autosave] Failed to perform emergency save on unmount:', err);
        });
      }
    };
  }, []);

  const value = {
    saveStatus,
    lastSaved
  };

  return (
    <AutosaveContext.Provider value={value}>
      {children}
    </AutosaveContext.Provider>
  );
}

export const useAutosave = () => {
  const ctx = useContext(AutosaveContext);
  if (!ctx) throw new Error('useAutosave must be used within a AutosaveProvider');
  return ctx;
};
